import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Users,
  ShieldCheck,
  FileText,
  CalendarDays,
  Cpu,
  Terminal,
  ArrowUpRight,
  Clock,
  Award,
  TrendingUp,
  CheckCircle,
} from "lucide-react";

const tabs = [
  {
    label: "Employee Master",
    path: "/admin/employee-master",
    color: "#6366F1",
    icon: Users,
    desc: "Workforce Identity",
  },
  {
    label: "Documents Vault",
    path: "/admin/documents-vault",
    color: "#10B981",
    icon: ShieldCheck,
    desc: "Secure Storage",
  },
  {
    label: "Letter Templates",
    path: "/admin/letter-template-mgmt",
    color: "#A855F7",
    icon: FileText,
    desc: "HR Automation",
  },
  {
    label: "Holiday Calendar",
    path: "/admin/holiday-calendar",
    color: "#EC4899",
    icon: CalendarDays,
    desc: "Global Leaves",
  },
  {
    label: "Biometric Setup",
    path: "/admin/biometric-integration-setup",
    color: "#06B6D4",
    icon: Cpu,
    desc: "Hardware Sync",
  },
  {
    label: "Processing Logs",
    path: "/admin/error-exception-logs",
    color: "#475569",
    icon: Terminal,
    desc: "System Health",
  },
  {
    label: "Shift Schedules",
    path: "/admin/shift-rotational-setup",
    color: "#F59E0B",
    icon: Clock,
    desc: "Shift & Rotations",
  },
  {
    label: "Appraisal Management",
    path: "/admin/appraisal-management",
    color: "#8B5CF6",
    icon: Award,
    desc: "Performance & Compensation",
  },
];

export default function PeopleOperationsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="p-8 bg-[#fafafa] min-h-screen font-roboto">
      {/* HEADER */}
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-2xl font-black font-poppins text-slate-900 tracking-tight">
          People <span className="text-indigo-600">Operations</span>
        </h1>
        <div className="h-1 w-12 bg-indigo-600 mt-1 rounded-full" />
      </div>

      {/* GRID */}
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {tabs.map((tab) => {
          const isActive = location.pathname === tab.path;
          const Icon = tab.icon;

          return (
            <div
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`
                cursor-pointer rounded-2xl p-4 h-40 relative
                flex flex-col justify-between border
                transition-all duration-200 ease-out
                ${
                  isActive
                    ? "border-indigo-500 scale-[1.03]"
                    : "border-slate-200 hover:-translate-y-0.5"
                }
              `}
              style={{
                backgroundColor: isActive ? tab.color : "#ffffff",
                boxShadow: isActive
                  ? `0 20px 40px -15px ${tab.color}AA`
                  : "0 6px 16px rgba(0,0,0,0.08)",
              }}
            >
              {/* TOP */}
              <div className="flex justify-between items-start">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    backgroundColor: isActive
                      ? "rgba(255,255,255,0.2)"
                      : `${tab.color}15`,
                    color: isActive ? "#ffffff" : tab.color,
                  }}
                >
                  <Icon size={20} strokeWidth={2.5} />
                </div>

                <ArrowUpRight
                  size={16}
                  className={isActive ? "text-white" : "text-slate-300"}
                />
              </div>

              {/* TEXT */}
              <div>
                <h3
                  className={`text-sm font-black font-poppins tracking-tight mb-0.5 ${
                    isActive ? "text-white" : "text-slate-800"
                  }`}
                >
                  {tab.label}
                </h3>
                <p
                  className={`text-[11px] font-bold uppercase tracking-tighter ${
                    isActive ? "text-white/80" : "text-slate-400"
                  }`}
                >
                  {tab.desc}
                </p>
              </div>

              {/* ACTIVE INDICATOR */}
              {isActive && (
                <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full animate-pulse" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
